import { urlIsBlocklisted } from '@fullstory/resource-blocklist';
import * as Regexes from '@fullstory/shared/regexes';

const CURRENT_URL_REPLACEMENT = 'https://fs-currenturl.invalid';

export type UrlRewriteOptions = {
  /**
   * If true, the rewritten URL must be same-origin with the playback document.
   * This is necessary to support some web platform features that don't support
   * CORS. Note that we can only honor this flag if the playback service worker
   * is enabled; otherwise, setting it will cause URL rewriting to throw.
   * Defaults to false.
   */
  requiresSameOrigin?: boolean;
};

/**
 * @returns true if the provided URL is blocked by the resource blocklist. We
 * only apply this check for 'https:' and 'http:' URLs because all other
 * protocols are blocked by the resource blocklist; that makes sense on the
 * server side, but not on the client, because we can load other types of
 * resources if they were captured in the resource map.
 */
function isBlocklisted(url: URL): boolean {
  return ['https:', 'http:'].includes(url.protocol) && urlIsBlocklisted(url);
}

function wrapInRedirect(absUrl: URL, requiresSameOrigin: boolean): string {
  // Save the fragment identifier of the original URL so we can add it to the
  // proxied version. The proxied URL needs to use the same hash so that media
  // fragments, SVG symbol references, and the like continue to work.
  const originalHash = absUrl.hash;
  absUrl.hash = '';

  // For the same-origin case, redirect using the same URL format as the old
  // same-origin proxy.
  // TODO(sethf): This will allow cross-origin resources to load even if the
  // original page would have blocked them. We should be smarter about this
  // and only allow this if the original page would have loaded the URL.
  if (requiresSameOrigin) {
    const redirectUrl = new URL(self.location.origin);
    redirectUrl.pathname = '/resource';
    redirectUrl.searchParams.set('Url', absUrl.href);
    redirectUrl.hash = originalHash;
    return redirectUrl.href;
  }

  // For the non-same-origin case, use an otherwise-invalid domain for the
  // redirect. The '.invalid' TLD is reserved and explicitly invalid, so it
  // won't resolve to anything if this somehow hits the network. (See RFC
  // 6761.)
  const redirectUrl = new URL('https://redirect.invalid');
  const protocol = absUrl.protocol.replace(/:$/, '');
  const pathHasLeadingSlash = absUrl.pathname.charAt(0) === '/';
  redirectUrl.hostname = `s-${protocol}.h-${absUrl.hostname}.p-${absUrl.port}.ls-${
    pathHasLeadingSlash ? 1 : 0
  }.redirect.invalid`;
  redirectUrl.pathname = absUrl.pathname;
  redirectUrl.search = absUrl.search;
  redirectUrl.hash = originalHash;
  return redirectUrl.href;
}

/**
 * Regex to extract the redirect url (this needs to mirror the encoding in wrapInRedirect())
 * Redirected url's hostname is in the following format:
 * s-[scheme].h-[original hostname].p-[port?].ls-[1 iff original path has a leading slash].redirect.invalid
 */
const extractRedirectedUrlRegex = /^s-([^.]*)\.h-(.*)\.p-(\d*)\.ls-(0|1)\.redirect\.invalid$/;
const redirectedUrlTestRegex = /redirect\.invalid$/;

/**
 * @returns true if 'url' is a special redirect URL of the sort that we use to
 * make non-HTTPS resources visible to the service worker.
 */
export function isRedirectUrl(url: URL) {
  return redirectedUrlTestRegex.test(url.hostname);
}

/**
 * Unwraps a url that was wrapped in redirect, or returns undefined if it's an unexpected format
 * @param url
 */
export function unwrapRedirect(url: URL): URL | undefined {
  const matches = url.hostname.match(extractRedirectedUrlRegex);
  if (!matches) {
    return undefined;
  }

  const host = matches[2];

  let protocol: string;
  switch (matches[1]) {
    case 'http':
    case 'file':
      // for http/file, we always want to use scheme://
      protocol = `${matches[1]}://`;
      break;
    default:
      // this depends on how browser parses URL with custom protocol (which differs between browsers)
      // chrome will parse ionic://foo/bar with pathname as //foo/bar, with no host
      // safari will parse the same url with path being /bar, and host being foo
      // so if we don't have a host we won't insert it again
      protocol = !host ? `${matches[1]}:` : `${matches[1]}://`;
  }

  const pathHasLeadingSlash = matches[4] === '1';
  const path =
    !pathHasLeadingSlash && url.pathname && url.pathname[0] === '/'
      ? url.pathname.substring(1)
      : url.pathname;

  const port = matches[3] ? `:${matches[3]}` : '';
  const urlString = `${protocol}${host}${port}${path}`;

  const unwrapped = new URL(urlString);
  unwrapped.search = url.search;
  unwrapped.hash = url.hash;

  return unwrapped;
}

/**
 * Rewrite a URL by applying transformations that block unsafe or blocklisted
 * URLs and make URLs that a normally invisible to the playback service worker
 * (e.g. blob URIs) interceptable.
 *
 * @param baseUrl - A base URL that will be used to convert relative URLs to
 * absolute URLs.
 * @param url - The URL to rewrite.
 * @param options - Various context-specific options that can affect the
 * rewriting process; see {@link UrlRewriteOptions} for more details.
 * @returns the rewritten URL; may be the empty string if the URL is unsafe or
 * blocklisted.
 */
export function rewriteUrl(baseUrl: string, url: string, options: UrlRewriteOptions = {}): string {
  if (url === '') {
    return ''; // Nothing to rewrite.
  }

  // If the url is _only_ a hash, leave it alone. It's a reference to
  // something in the current page.
  if (url.startsWith('#')) {
    return url;
  }

  // Ensure that the URL is absolute so we can determine the URI scheme.
  let absUrl: URL;
  try {
    absUrl = new URL(url, baseUrl);
  } catch (e) {
    console.warn(`Invalid URL '${url}' with base '${baseUrl}': ${e}`);
    return '';
  }

  // If this URL is explicitly blocklisted, block it.
  if (isBlocklisted(absUrl)) {
    return '';
  }

  const requiresSameOrigin = options.requiresSameOrigin ?? false;

  switch (absUrl.protocol) {
    case 'https:':
      // If the url was referring to a hash within the same page,
      // but it included the entire url, just return the hash.
      if (absUrl.origin === CURRENT_URL_REPLACEMENT) {
        return absUrl.hash;
      }

      if (requiresSameOrigin) {
        return wrapInRedirect(absUrl, requiresSameOrigin);
      }
      return url; // No special handling required.

    case 'javascript:':
      return ''; // Forbid JavaScript code embedded in URLs.

    case 'data:': {
      const dataUriParts = Regexes.DATA_URI.exec(url);
      if (dataUriParts) {
        if (
          dataUriParts[1]!.match(Regexes.SKETCHY_CONTENT_TYPES) &&
          !dataUriParts[1]!.match(Regexes.ALLOWLISTED_CONTENT_TYPES)
        ) {
          return ''; // Forbid sketchy content types (e.g., 'text/js').
        }
      }

      return url; // Otherwise, leave data: URIs alone.
    }

    default:
      // These URI schemes can't be intercepted by the service worker. We can
      // make them visible by wrapping them in a redirect URL that *is*
      // interceptable.
      return wrapInRedirect(absUrl, requiresSameOrigin);
  }
}

/**
 * A UrlRewriter rewrites URLs using a fixed base URL. There should typically
 * be one of them per document, stylesheet, or similar resource. If you need to
 * rewrite arbitrary URLs with different bases, use {@link rewriteUrl} directly.
 * (On the other hand, if the base URL of the resource changes, as it sometimes
 * does during playback, you can set the baseUrl property to keep your
 * UrlRewriter up to date.)
 */
export class UrlRewriter {
  constructor(public baseUrl: string) {}

  rewriteUrl(url: string, options: UrlRewriteOptions = {}): string {
    return rewriteUrl(this.baseUrl, url, options);
  }
}
